import { useSignal } from "@preact/signals";
import {
  Button,
  DatePicker,
  Input,
  ScheduleSelector,
} from "../../../../components/ui";

type SchedulePanelProps = {
  rrule: string | null;
  startsAt: Date | null;
  endsAt: Date | null;
  submissionWindowDays: number | null;
  onChange: (updates: {
    rrule?: string | null;
    startsAt?: Date | null;
    endsAt?: Date | null;
    submissionWindowDays?: number | null;
  }) => void;
  onClose: () => void;
};

export function SchedulePanel({
  rrule,
  startsAt,
  endsAt,
  submissionWindowDays,
  onChange,
  onClose,
}: SchedulePanelProps) {
  // Local state for immediate UI feedback
  const localRrule = useSignal<string | null>(rrule);
  const localStartsAt = useSignal<Date | null>(startsAt);
  const localEndsAt = useSignal<Date | null>(endsAt);
  const localWindowDays = useSignal<number | null>(submissionWindowDays);

  return (
    <div class="flex flex-col gap-6">
      <ScheduleSelector
        rrule={localRrule.value}
        onChange={(newRrule) => {
          localRrule.value = newRrule;
          if (!newRrule && localWindowDays.value !== null) {
            localWindowDays.value = null;
            onChange({ rrule: newRrule, submissionWindowDays: null });
          } else {
            onChange({ rrule: newRrule });
          }
        }}
      />

      {/* Date inputs */}
      <div class="grid grid-cols-2 gap-4">
        <DatePicker
          label={localRrule.value ? "Starts" : "Available from"}
          value={localStartsAt.value}
          onChange={(date) => {
            localStartsAt.value = date;
            onChange({ startsAt: date });
          }}
          placeholder="Today"
        />
        <DatePicker
          label={localRrule.value ? "Ends" : "Available until"}
          value={localEndsAt.value}
          onChange={(date) => {
            localEndsAt.value = date;
            onChange({ endsAt: date });
          }}
          placeholder="Never"
        />
      </div>

      {/* Submission window (only for recurring forms) */}
      {localRrule.value && (
        <div class="flex flex-col gap-1">
          <Input
            label="Submission window (days before due)"
            type="number"
            min={1}
            max={30}
            placeholder="Full period"
            value={localWindowDays.value ?? ""}
            onInput={(e: Event) => {
              const val = (e.target as HTMLInputElement).value;
              const parsed = val ? Number.parseInt(val, 10) : null;
              const days =
                parsed !== null && Number.isNaN(parsed) ? null : parsed;
              localWindowDays.value = days;
              onChange({ submissionWindowDays: days });
            }}
          />
          <p class="text-xs text-text-muted">
            How many days before the due date should the form open? Leave blank
            for the entire period.
          </p>
        </div>
      )}

      {/* Done button */}
      <div class="sticky bottom-0 -mx-6 px-6 py-4 border-t border-border bg-surface">
        <Button type="button" variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
